import { starlinkTokens } from "@zr-eduai/star-design-tokens";

const INTRO_ELEMENT_NAME = "star-intro-overlay";
const HTMLElementBase = (typeof HTMLElement === "undefined" ? class {} : HTMLElement) as typeof HTMLElement;

export type StarIntroAudienceState =
  | "guest"
  | "unentitled"
  | "unauthorized"
  | "authorized-first-entry"
  | "authorized-reopen";

export type StarIntroStepItemV1 = {
  label: string;
  description?: string;
};

export type StarIntroStepV1 = {
  title: string;
  description?: string;
  items?: readonly StarIntroStepItemV1[];
};

export type StarIntroDefinitionV1 = {
  introId: string;
  introVersion: string;
  kind: "learning-line" | "module" | "application" | "experience";
  eyebrow?: string;
  title: string;
  summary: string;
  contextLabel?: string;
  poster?: string;
  posterAlt?: string;
  posterTitle?: string;
  posterStateLabel?: string;
  steps: readonly StarIntroStepV1[];
  primaryLabel?: string;
  secondaryLabel?: string;
  footnote?: string;
};

export type StarIntroDismissReason = "close" | "later" | "escape" | "primary";

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;"
  })[character]!);
}

function focusedElementForRestore(): HTMLElement | null {
  let active = document.activeElement;
  while (active instanceof HTMLElement) {
    const nested = active.shadowRoot?.activeElement;
    if (!(nested instanceof HTMLElement)) return active;
    active = nested;
  }
  return null;
}

function styles(): string {
  return `
    :host { all:initial; display:none; position:fixed; inset:${starlinkTokens.barHeight} 0 0; z-index:2147482500; font-family:${starlinkTokens.fontFamily}; color-scheme:dark; }
    :host([open]) { display:block; }
    *, *::before, *::after { box-sizing:border-box; }
    button { font:inherit; }
    .scrim { width:100%; height:100%; display:grid; place-items:center; padding:22px; color:#eef8ff; background:rgba(1,8,18,.78); }
    .dialog { width:min(980px,100%); max-height:calc(100dvh - ${starlinkTokens.barHeight} - 44px); display:grid; grid-template-rows:auto minmax(0,1fr) auto; overflow:hidden; border:1px solid rgba(52,211,255,.72); border-radius:16px; background:#06172b; box-shadow:0 28px 80px rgba(0,0,0,.48),0 0 36px rgba(25,180,255,.12); }
    .header { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding:21px 24px 14px; }
    .header > div:first-child { min-width:0; }
    .eyebrow { margin:0 0 6px; color:#b6c8d9; font-size:12px; letter-spacing:.08em; }
    h2 { margin:0; color:#fff; font-size:clamp(28px,2.4vw,34px); font-weight:760; line-height:1.08; letter-spacing:-.035em; }
    .summary { margin:7px 0 0; color:#b9c9d8; font-size:14px; line-height:1.55; }
    .header-side { display:flex; align-items:center; gap:14px; }
    .context { min-height:36px; display:inline-flex; align-items:center; padding:0 16px; border:1px solid rgba(116,170,226,.48); border-radius:999px; color:#d5e8ff; background:#09203a; font-size:13px; white-space:nowrap; }
    .close { min-width:52px; height:36px; flex:0 0 auto; padding:0 10px; border:1px solid transparent; border-radius:9px; color:#d7e5f2; background:transparent; cursor:pointer; white-space:nowrap; }
    .close:hover { border-color:rgba(111,185,235,.38); background:#0b2844; }
    .body { min-height:0; display:grid; grid-template-columns:minmax(180px,205px) minmax(0,1fr); gap:18px; overflow:auto; padding:0 24px 14px; }
    .poster { min-height:278px; display:grid; grid-template-rows:minmax(0,1fr) auto; overflow:hidden; margin:0; border:1px solid rgba(71,174,231,.65); border-radius:12px; background:#081e35; }
    .poster img { width:100%; height:100%; min-height:0; display:block; object-fit:cover; }
    .poster figcaption { padding:11px 13px; border-top:1px solid rgba(99,164,210,.3); background:#091a2c; }
    .poster strong { display:block; color:#f5fbff; font-size:16px; }
    .poster small { display:block; margin-top:4px; color:#83e7b0; font-size:11px; }
    .flow { min-width:0; display:grid; grid-template-columns:repeat(var(--intro-step-count,3),minmax(0,1fr)); gap:10px; margin:0; padding:32px 0 0; list-style:none; }
    .stage { position:relative; min-width:0; display:flex; flex-direction:column; padding:17px 12px 12px; border:1px solid rgba(72,172,225,.66); border-radius:12px; background:#092038; }
    .stage-number { position:absolute; top:-30px; left:50%; width:27px; height:27px; display:grid; place-items:center; border:2px solid #72bfff; border-radius:50%; color:#cfeaff; background:#092038; font-size:13px; font-weight:760; transform:translateX(-50%); }
    .stage h3 { margin:0; color:#f1f8ff; font-size:15px; line-height:1.35; }
    .stage > p { min-height:34px; margin:5px 0 10px; color:#93a9bd; font-size:11px; line-height:1.5; }
    .items { display:grid; gap:9px; margin-top:auto; }
    .item { min-height:56px; padding:10px; border:1px solid rgba(141,185,219,.38); border-radius:9px; background:#0c2944; }
    .item strong { display:block; color:#eef7ff; font-size:13px; }
    .item span { display:block; margin-top:4px; color:#9eb3c7; font-size:10px; line-height:1.4; }
    .footer { display:grid; grid-template-columns:1fr auto 1fr; align-items:center; gap:14px; padding:0 24px 12px; }
    .primary, .secondary { min-height:44px; border-radius:10px; cursor:pointer; }
    .primary { min-width:260px; padding:0 24px; border:1px solid #36d8ff; color:#001422; background:#25c8ef; font-size:16px; font-weight:800; }
    .primary:hover { background:#58dcf7; }
    .secondary { padding:0 18px; border:1px solid transparent; color:#c4d2df; background:transparent; font-size:14px; }
    .secondary:hover { border-color:rgba(116,170,226,.34); background:#0b2844; }
    .footnote { grid-column:1/-1; margin:-8px 0 0; color:#71869a; font-size:12px; text-align:center; }
    .close:focus-visible, .primary:focus-visible, .secondary:focus-visible { outline:3px solid rgba(56,189,248,.42); outline-offset:2px; }
    @media (max-width:900px) {
      .scrim { padding:12px; }
      .dialog { max-height:calc(100dvh - ${starlinkTokens.barHeightMobile} - 24px); }
      .header { padding:20px 20px 15px; }
      .context { display:none; }
      .body { grid-template-columns:1fr; padding:0 20px 16px; }
      .poster { min-height:210px; grid-template-columns:180px minmax(0,1fr); grid-template-rows:1fr; }
      .poster figcaption { display:flex; flex-direction:column; justify-content:center; border-top:0; border-left:1px solid rgba(99,164,210,.3); }
      .flow { overflow-x:auto; grid-template-columns:repeat(var(--intro-step-count,3),minmax(230px,1fr)); padding-top:36px; scrollbar-width:thin; }
      .footer { grid-template-columns:1fr; gap:6px; padding:0 20px 14px; }
      .primary { width:100%; min-width:0; }
      .secondary { width:100%; }
    }
    @media (max-width:560px) {
      :host { inset:${starlinkTokens.barHeightMobile} 0 0; }
      .scrim { padding:0; }
      .dialog { width:100%; height:100%; max-height:none; border-width:1px 0 0; border-radius:0; }
      .header { padding:18px 16px 14px; }
      h2 { font-size:28px; }
      .body { padding-inline:16px; }
      .poster { min-height:170px; grid-template-columns:132px minmax(0,1fr); }
      .footer { padding-inline:16px; }
    }
    @media (prefers-reduced-motion:reduce) { *, *::before, *::after { scroll-behavior:auto !important; } }
  `;
}

const fallbackDefinition: StarIntroDefinitionV1 = {
  introId: "star-intro",
  introVersion: "1",
  kind: "application",
  eyebrow: "首次进入",
  title: "认识当前应用",
  summary: "先了解这段学习旅程，再开始使用。",
  steps: [],
};

export class StarIntroOverlayElement extends HTMLElementBase {
  static observedAttributes = ["open", "primary-label", "secondary-label"];

  #definition: StarIntroDefinitionV1 = fallbackDefinition;
  #audience: StarIntroAudienceState = "guest";
  #lastFocused: HTMLElement | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  get definition(): StarIntroDefinitionV1 {
    return this.#definition;
  }

  set definition(value: StarIntroDefinitionV1) {
    this.#definition = value ?? fallbackDefinition;
    if (this.isConnected) this.render();
  }

  get audience(): StarIntroAudienceState {
    return this.#audience;
  }

  set audience(value: StarIntroAudienceState) {
    this.#audience = value;
    if (this.isConnected) this.render();
  }

  get open(): boolean {
    return this.hasAttribute("open");
  }

  set open(value: boolean) {
    this.toggleAttribute("open", value);
  }

  connectedCallback() {
    if (this.open && !this.#lastFocused) {
      this.#lastFocused = focusedElementForRestore();
    }
    this.render();
    if (this.open) this.focusPrimarySoon();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (!this.isConnected) return;
    const opening = name === "open" && oldValue === null && newValue !== null;
    const closing = name === "open" && oldValue !== null && newValue === null;
    if (opening && !this.#lastFocused) {
      this.#lastFocused = focusedElementForRestore();
    }
    this.render();
    if (opening) this.focusPrimarySoon();
    if (closing) {
      this.#lastFocused?.focus();
      this.#lastFocused = null;
    }
  }

  private focusPrimarySoon() {
    queueMicrotask(() => this.shadowRoot?.querySelector<HTMLButtonElement>(".primary")?.focus());
  }

  show() {
    if (this.open) return;
    this.#lastFocused = focusedElementForRestore();
    this.open = true;
  }

  close(reason: StarIntroDismissReason = "close") {
    if (!this.open) return;
    const accepted = this.dispatchEvent(new CustomEvent("star-intro-dismiss", {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        introId: this.#definition.introId,
        introVersion: this.#definition.introVersion,
        audience: this.#audience,
        reason,
      },
    }));
    if (!accepted) return;
    this.open = false;
  }

  private focusableItems(): HTMLButtonElement[] {
    return Array.from(this.shadowRoot?.querySelectorAll<HTMLButtonElement>("button") ?? []);
  }

  private onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close("escape");
      return;
    }
    if (event.key !== "Tab") return;
    const items = this.focusableItems();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = this.shadowRoot?.activeElement as HTMLButtonElement | null;
    if (!active || !items.includes(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private render() {
    const definition = this.#definition;
    const primaryLabel = this.getAttribute("primary-label")?.trim() || definition.primaryLabel || "开始使用";
    const secondaryLabel = this.getAttribute("secondary-label")?.trim() || definition.secondaryLabel || "稍后再看";
    const posterTitle = definition.posterTitle || definition.contextLabel || definition.title;
    const posterState = definition.posterStateLabel || (this.#audience === "unentitled"
      ? "学校未开通"
      : this.#audience === "unauthorized"
        ? "账号未授权"
        : this.#audience === "guest"
          ? "登录后确认"
          : "可开始使用");
    const steps = definition.steps.slice(0, 4);
    const stepCount = Math.max(steps.length, 1);

    this.shadowRoot!.innerHTML = `
      <style>${styles()}</style>
      <div class="scrim">
        <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="star-intro-title" aria-describedby="star-intro-summary" tabindex="-1">
          <header class="header">
            <div>
              <p class="eyebrow">${escapeHtml(definition.eyebrow || "首次进入")}</p>
              <h2 id="star-intro-title">${escapeHtml(definition.title)}</h2>
              <p class="summary" id="star-intro-summary">${escapeHtml(definition.summary)}</p>
            </div>
            <div class="header-side">
              ${definition.contextLabel ? `<span class="context">${escapeHtml(definition.contextLabel)}</span>` : ""}
              <button class="close" type="button" aria-label="关闭介绍">关闭</button>
            </div>
          </header>
          <div class="body">
            ${definition.poster ? `<figure class="poster"><img src="${escapeHtml(definition.poster)}" alt="${escapeHtml(definition.posterAlt || "")}" /><figcaption><strong>${escapeHtml(posterTitle)}</strong><small>${escapeHtml(posterState)}</small></figcaption></figure>` : ""}
            <ol class="flow" style="--intro-step-count:${stepCount}">
              ${steps.map((step, index) => `<li class="stage"><span class="stage-number" aria-hidden="true">${index + 1}</span><h3>${escapeHtml(step.title)}</h3>${step.description ? `<p>${escapeHtml(step.description)}</p>` : "<p></p>"}<div class="items">${(step.items || []).map((item) => `<div class="item"><strong>${escapeHtml(item.label)}</strong>${item.description ? `<span>${escapeHtml(item.description)}</span>` : ""}</div>`).join("")}</div></li>`).join("")}
            </ol>
          </div>
          <footer class="footer">
            <span aria-hidden="true"></span>
            <button class="primary" type="button">${escapeHtml(primaryLabel)}</button>
            <button class="secondary" type="button">${escapeHtml(secondaryLabel)}</button>
            <p class="footnote">${escapeHtml(definition.footnote || "以后可从“？”重新查看")}</p>
          </footer>
        </section>
      </div>`;

    const dialog = this.shadowRoot!.querySelector<HTMLElement>(".dialog");
    dialog?.addEventListener("keydown", (event) => this.onKeyDown(event));
    this.shadowRoot!.querySelector<HTMLButtonElement>(".close")?.addEventListener("click", () => this.close("close"));
    this.shadowRoot!.querySelector<HTMLButtonElement>(".secondary")?.addEventListener("click", () => this.close("later"));
    this.shadowRoot!.querySelector<HTMLButtonElement>(".primary")?.addEventListener("click", () => {
      const accepted = this.dispatchEvent(new CustomEvent("star-intro-primary-action", {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: {
          introId: definition.introId,
          introVersion: definition.introVersion,
          audience: this.#audience,
        },
      }));
      if (accepted) this.close("primary");
    });
    this.shadowRoot!.querySelector<HTMLElement>(".scrim")?.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) this.close("close");
    });
  }
}

if (typeof customElements !== "undefined" && !customElements.get(INTRO_ELEMENT_NAME)) {
  customElements.define(INTRO_ELEMENT_NAME, StarIntroOverlayElement);
}

declare global {
  interface HTMLElementTagNameMap {
    "star-intro-overlay": StarIntroOverlayElement;
  }
}

export { INTRO_ELEMENT_NAME };
